import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, Anchor, TrendingUp, CalendarRange, Users, Menu, X, Filter, Globe, Megaphone, Send } from 'lucide-react';

const NAV = [
  { id: 'home', label: 'Home', icon: Home, path: '/intelligence' },
  { group: 'Reports' },
  { id: 'asset-earnings', label: 'Asset Earnings', icon: Anchor, path: '/intelligence/asset-earnings' },
  { id: 'service-sales-mix', label: 'Revenue Mix', icon: TrendingUp, path: '/intelligence/service-sales-mix' },
  { id: 'demand-calendar', label: 'Demand Center', icon: CalendarRange, path: '/intelligence/demand-calendar' },
  { id: 'customer-insights', label: 'Customer Insights', icon: Users, path: '/intelligence/customer-insights' },
  { group: 'Acquisition' },
  { id: 'booking-funnel', label: 'Booking Funnel', icon: Filter, path: '/intelligence/booking-funnel' },
  { id: 'website-analytics', label: 'Website Analytics', icon: Globe, path: '/intelligence/website-analytics' },
  { id: 'audiences', label: 'Audiences', icon: Megaphone, path: '/intelligence/audiences' },
  { group: 'Advertising' },
  { id: 'campaigns', label: 'Campaigns', icon: Send, path: '/intelligence/campaigns' },
];

const Brand = ({ onClick, small = false }) => (
  <button onClick={onClick} className="flex items-center gap-2" data-testid="intelligence-brand">
    <img src="/bf-intelligence-logo.png" alt="Helm Intelligence" className={`${small ? 'w-6 h-6' : 'w-7 h-7'} object-contain`} />
    <span className="flex flex-col items-start leading-none">
      <span className="font-bold text-sm tracking-tight text-slate-900 whitespace-nowrap">
        HELM <span className="italic">Intelligence</span>
      </span>
      <span className="text-[9px] font-semibold text-slate-400 tracking-wide mt-0.5">by BookingFlows</span>
    </span>
  </button>
);

const NavItems = ({ pathname, onNavigate, idPrefix = '' }) => (
  <>
    {NAV.map((item, i) => {
      if (item.group) {
        return (
          <div key={i} className="px-4 pt-6 pb-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">
            {item.group}
          </div>
        );
      }
      const Icon = item.icon;
      const active = item.path && pathname === item.path;
      return (
        <button
          key={item.id}
          data-testid={`${idPrefix}intelligence-nav-${item.id}`}
          disabled={item.locked}
          onClick={() => item.path && onNavigate(item.path)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-left transition-colors duration-150 ${
            active
              ? 'bg-[#EBF2FF] text-[#2563EB] font-bold'
              : item.locked
                ? 'text-slate-400 cursor-default'
                : 'text-slate-700 hover:bg-white/80 font-semibold'
          }`}
        >
          <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-150 ${
            active ? 'bg-[#2563EB] text-white' : item.locked ? 'bg-slate-100/60 text-slate-300' : 'bg-slate-100 text-slate-600'
          }`}>
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </span>
          <span className="truncate">{item.label}</span>
          {item.soon && (
            <span className="ml-auto px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#2563EB] bg-[#EBF2FF] rounded-full">Soon</span>
          )}
        </button>
      );
    })}
  </>
);

const BackToDashboard = ({ onClick, idPrefix = '' }) => (
  <button
    data-testid={`${idPrefix}intelligence-back-to-dashboard`}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white/80 transition-colors duration-150"
  >
    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
      <ArrowLeft className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
    </span>
    Back to Dashboard
  </button>
);

const WelcomeOverlay = ({ onDone }) => {
  const [stage, setStage] = useState('loading');
  useEffect(() => {
    const t1 = setTimeout(() => setStage('ready'), 1900);
    const t2 = setTimeout(() => setStage('fade'), 2750);
    const t3 = setTimeout(onDone, 3300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);
  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#FBFCFF] transition-opacity duration-500 ${stage === 'fade' ? 'opacity-0' : 'opacity-100'}`}
      style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}
      data-testid="intelligence-welcome-overlay"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-20 -left-20 w-64 h-64 sm:w-[560px] sm:h-[560px] sm:-top-32 sm:-left-32 bg-blue-300/30 rounded-full blur-3xl intel-glow" />
        <div className="absolute -bottom-24 -right-20 w-56 h-56 sm:w-[520px] sm:h-[520px] sm:-bottom-40 sm:-right-32 bg-indigo-300/25 rounded-full blur-3xl intel-glow" style={{ animationDelay: '1s' }} />
        <div className="absolute -bottom-16 -left-12 w-44 h-44 sm:w-[380px] sm:h-[380px] sm:-bottom-32 sm:-left-24 bg-sky-200/25 rounded-full blur-3xl intel-glow" style={{ animationDelay: '0.5s' }} />
      </div>

      <img
        src="/bf-intelligence-logo.png"
        alt="Helm Intelligence"
        className="w-14 h-14 sm:w-20 sm:h-20 object-contain intel-logo-in relative"
        style={{ filter: 'drop-shadow(0 10px 34px rgba(37,99,235,0.35))' }}
      />
      <div className="mt-3 text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 intel-rise relative" style={{ animationDelay: '250ms' }}>
        HELM <span className="italic">Intelligence</span>
      </div>
      <div className="mt-1 text-[11px] font-semibold text-slate-400 tracking-wide intel-rise relative" style={{ animationDelay: '320ms' }}>
        by BookingFlows
      </div>
      <div key={stage} className="mt-3 h-4 text-xs font-semibold text-slate-400 intel-rise relative px-6 text-center" data-testid="intelligence-welcome-status">
        {stage === 'loading' ? 'Loading your intelligence — querying your bookings…' : 'Your intelligence is ready'}
      </div>
      <div className="mt-6 w-48 h-1 rounded-full bg-slate-100 overflow-hidden relative intel-rise" style={{ animationDelay: '400ms' }}>
        <div className="h-full rounded-full bg-[#2563EB] intel-bar-fill" style={{ animationDelay: '450ms', animationDuration: '1.5s' }} />
      </div>
    </div>
  );
};

export const IntelligenceShell = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [welcoming, setWelcoming] = useState(() => Date.now() - (window.__bfIntelLastSeen || 0) > 600);
  useEffect(() => {
    window.__bfIntelLastSeen = Date.now();
    const id = setInterval(() => { window.__bfIntelLastSeen = Date.now(); }, 150);
    return () => { clearInterval(id); window.__bfIntelLastSeen = Date.now(); };
  }, []);
  const dismissWelcome = () => setWelcoming(false);
  const go = (path) => { setDrawerOpen(false); navigate(path); };

  return (
    <div className="min-h-screen flex bg-[#FBFCFF] relative" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid="intelligence-shell">
      {welcoming && <WelcomeOverlay onDone={dismissWelcome} />}

      {/* Floaty gradient backdrop */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -left-20 w-56 h-56 sm:w-[560px] sm:h-[560px] sm:-top-32 sm:-left-32 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-48 h-48 sm:w-[480px] sm:h-[480px] sm:-right-40 bg-indigo-200/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 left-1/3 w-52 h-52 sm:w-[520px] sm:h-[520px] sm:-bottom-40 sm:left-1/4 bg-sky-200/20 rounded-full blur-3xl" />
      </div>

      {/* Desktop fixed sidebar with brand */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 bg-white/50 backdrop-blur-xl border-r border-slate-200/50 md:sticky md:top-0 md:self-start md:h-screen relative z-10"
        data-testid="intelligence-sidebar"
      >
        <div className="px-4 pt-5 pb-3 shrink-0">
          <Brand onClick={() => navigate('/intelligence')} />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          <NavItems pathname={pathname} onNavigate={go} />
        </nav>
        <div className="p-3 border-t border-slate-100/70 shrink-0">
          <BackToDashboard onClick={() => navigate('/admin')} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <div className={`md:hidden fixed inset-0 z-50 ${drawerOpen ? '' : 'pointer-events-none'}`} aria-hidden={!drawerOpen}>
        <div
          className={`absolute inset-0 bg-slate-900/30 backdrop-blur-sm transition-opacity duration-200 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawerOpen(false)}
          data-testid="intelligence-drawer-backdrop"
        />
        <div
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          data-testid="intelligence-drawer"
        >
          <div className="px-4 pt-5 pb-3 flex items-center justify-between shrink-0" style={{ paddingTop: 'max(1.25rem, env(safe-area-inset-top, 1.25rem))' }}>
            <Brand small onClick={() => go('/intelligence')} />
            <button
              onClick={() => setDrawerOpen(false)}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
              data-testid="intelligence-drawer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
            <NavItems pathname={pathname} onNavigate={go} idPrefix="drawer-" />
          </nav>
          <div className="p-3 border-t border-slate-100 shrink-0">
            <BackToDashboard onClick={() => navigate('/admin')} idPrefix="drawer-" />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 relative z-10">
        {/* Mobile header: brand + hamburger */}
        <div className="md:hidden sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-slate-200/50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="px-4 py-2.5 flex items-center justify-between">
            <Brand small onClick={() => navigate('/intelligence')} />
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-700"
              data-testid="intelligence-mobile-menu"
            >
              <Menu className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
};

export const MonoNum = ({ children, className = '' }) => (
  <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>{children}</span>
);
